import {
  type Maybe,
  type FilterNodeInput,
  type FilterNodeModel as _FilterNodeModel,
  FilterLogicalOperator,
  FilterOperator,
} from '@/generated/graphql.ts'
import { createTmpClientId } from '@/common/network/gql-types.ts'
import type { PropertyValue } from '@/objects/gql/PropertyValue.ts'

type FilterNodeModelBase = Omit<_FilterNodeModel, 'id' | 'parentId' | 'value' | 'values'> & {
  id: number
  parentId: Maybe<number>
}

export type GroupFilterNodeModel = FilterNodeModelBase & {
  logicalOperator: FilterLogicalOperator
  operator: null
  propertyDefinitionId: null
  value: null
  values: null
}

export type OperatorFilterNodeModel = FilterNodeModelBase & {
  logicalOperator: null
  operator: FilterOperator
  propertyDefinitionId: number
  value: PropertyValue | null
  values: number[] | null
}

/** Incomplete leaf node — exists only during UI editing before operator/property are chosen */
export type IncompleteFilterNodeModel = FilterNodeModelBase & {
  logicalOperator: null
  operator: null
  propertyDefinitionId: null
  value: PropertyValue | null
  values: number[] | null
}

export type FilterNodeModel =
  | GroupFilterNodeModel
  | OperatorFilterNodeModel
  | IncompleteFilterNodeModel

export function isGroupFilterNode(node: FilterNodeModel): node is GroupFilterNodeModel {
  return node.logicalOperator != null
}

export function isOperatorFilterNode(node: FilterNodeModel): node is OperatorFilterNodeModel {
  return node.operator != null
}

export function toFilterNodeInput(node: FilterNodeModel): FilterNodeInput {
  return {
    clientId: String(node.id),
    parentClientId: node.parentId != null ? String(node.parentId) : null,
    logicalOperator: node.logicalOperator,
    operator: node.operator,
    propertyDefinitionId: node.propertyDefinitionId,
    value: isOperatorFilterNode(node) && node.value != null ? String(node.value) : null,
    values: isOperatorFilterNode(node) && node.values != null ? node.values.map(String) : null,
  }
}

export function makeFilterLeaf(parentId: number | null): IncompleteFilterNodeModel {
  return {
    id: createTmpClientId(),
    parentId,
    logicalOperator: null,
    operator: null,
    propertyDefinitionId: null,
    value: null,
    values: null,
  }
}

export function makeFilterGroup(parentId: number | null): GroupFilterNodeModel {
  return {
    id: createTmpClientId(),
    parentId,
    logicalOperator: FilterLogicalOperator.And,
    operator: null,
    propertyDefinitionId: null,
    value: null,
    values: null,
  }
}
